// src/pages/MovesPage.js (full updated)
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import Modal from "../components/Modal";

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function MovesPage() {
  const [moves, setMoves] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drvById, setDrvById] = useState({});
  const [selectedMove, setSelectedMove] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // inventory hydration cache: { [id]: record|null }
  const [invById, setInvById] = useState({});

  // Form fields for create/edit
  const [destination, setDestination] = useState("");
  const [airline, setAirline] = useState("");
  const [flightNum, setFlightNum] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [permits, setPermits] = useState("");
  const [driverId, setDriverId] = useState("");
  const [notes, setNotes] = useState("");
  const [inventoryId, setInventoryId] = useState("");
  const [rowo, setRowo] = useState("");
  const [chassisVin, setChassisVin] = useState("");
  const [moveType, setMoveType] = useState("factory_to_sna");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [destinations, setDestinations] = useState([]);

  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  // Breakdown state
  // { [inventory_id]: openBreakdownRow | null }
  const [openBreakdownByUnit, setOpenBreakdownByUnit] = useState({});
  const [breakdownMove, setBreakdownMove] = useState(null);     // move currently being reported on
  const [resolveBreakdown, setResolveBreakdown] = useState(null); // breakdown currently being resolved

  useEffect(() => {
    loadMoves();
    loadDrivers();
    loadOpenBreakdowns();
    loadDestinations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDestinations = async () => {
    try {
      const { data } = await api.get("/destinations", { params: { active: "1" } });
      setDestinations(Array.isArray(data?.destinations) ? data.destinations : []);
    } catch (_) { /* non-fatal; admin can still type destination */ }
  };

  const loadOpenBreakdowns = async () => {
    try {
      const { data } = await api.get("/breakdowns", { params: { status: "open" } });
      const byUnit = {};
      for (const b of (data?.breakdowns || [])) byUnit[b.inventory_id] = b;
      setOpenBreakdownByUnit(byUnit);
    } catch (e) {
      console.error("Failed to load breakdowns", e);
    }
  };

  const hydrateInventoryForMoves = async (list) => {
    const ids = Array.from(
      new Set(
        (list || [])
          .map((m) => toNumId(m.inventory_id))
          .filter((n) => n != null)
      )
    );

    // fetch only missing ids
    const missing = ids.filter((id) => !(id in invById));
    if (!missing.length) return;

    // parallel fetch; store null on failure to avoid retry loops
    const fetched = await Promise.all(
      missing.map(async (id) => {
        try {
          const { data } = await api.get(`/all_inventory/${id}`);
          return [id, data || null];
        } catch {
          return [id, null];
        }
      })
    );

    setInvById((prev) => {
      const next = { ...prev };
      for (const [id, rec] of fetched) next[id] = rec;
      return next;
    });
  };

  const loadMoves = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/moves");
      let list = Array.isArray(data) ? data : [];
      if (!showCompleted) list = list.filter((m) => !m.completed_at);
      // Sort: active first (null completed_at), then most-recently completed first.
      list.sort((a, b) => {
        if (!a.completed_at && b.completed_at) return -1;
        if (a.completed_at && !b.completed_at) return 1;
        if (a.completed_at && b.completed_at) return String(b.completed_at).localeCompare(String(a.completed_at));
        return Number(b.TripId) - Number(a.TripId);
      });
      setMoves(list);
      await hydrateInventoryForMoves(list);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load moves.");
      setMoves([]);
    } finally {
      setBusy(false);
    }
  };

  // Reload when the toggle flips
  useEffect(() => { loadMoves(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showCompleted]);

  const loadDrivers = async () => {
    try {
      const { data } = await api.get("/driver");
      const list = Array.isArray(data) ? data : [];
      setDrivers(list);
      const byId = {};
      for (const d of list) byId[d.DriverId] = d;
      setDrvById(byId);
    } catch (e) {
      console.error("Failed to load drivers", e);
    }
  };

  const createOrUpdateMove = async () => {
    if (!inventoryId) {
      setMsg("Inventory ID required.");
      return;
    }

    const payload = {
      Destination: destination,
      Airline: airline,
      FlightNum: flightNum,
      Confirmation: confirmation,
      Permits: permits,
      driver_id: toNumId(driverId) || null,
      Notes: notes,
      inventory_id: inventoryId,
      rowo: rowo,
      chassis_vin: chassisVin,
      move_type: moveType,
      start_date: startDate || null,
      end_date:   endDate   || null,
      destination_id: destinationId || null,
    };

    setBusy(true);
    setMsg("");
    try {
      if (editMode) {
        await api.put(`/moves/${selectedMove.TripId}`, payload);
        setMsg("Move updated successfully.");
      } else {
        await api.post("/moves", payload);
        setMsg("Move created successfully.");
      }

      // refresh list + ensure the selected inventory record is hydrated
      resetForm();
      await loadMoves();
      closeModal();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteMove = async (id) => {
    if (!window.confirm("Delete this move?")) return;
    setBusy(true);
    try {
      await api.delete(`/moves/${id}`);
      setMsg("Move deleted.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  const completeMove = async (moveId, mt) => {
    const prompt = mt === "sna_to_customer"
      ? "Complete this move and mark the unit delivered to the customer?\n\n(PDI must be complete — otherwise this will fail.)"
      : "Complete this move and mark the unit arrived at SNA?";
    if (!window.confirm(prompt)) return;
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/moves/${moveId}/complete`);
      const newState = data?.transition?.new_state;
      setMsg(newState ? `Move completed — unit state: ${newState}` : "Move completed.");
      await loadMoves();
    } catch (e) {
      const err = e?.response?.data;
      const full = err?.hint ? `${err.error}\n\n${err.hint}` : (err?.error || "Failed to complete move.");
      setMsg(full);
    } finally {
      setBusy(false);
    }
  };

  const endMove = async (moveId, invId, statusCode) => {
    if (!window.confirm(`Confirm: Set inventory status to ${statusCode} and end move?`)) return;
    setBusy(true);
    setMsg("");
    try {
      // Update inventory status
      await api.put(`/all_inventory/${invId}`, { color_code: statusCode });

      // Delete move
      await api.delete(`/moves/${moveId}`);

      setMsg("Move ended successfully.");
      await loadMoves();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to end move.");
    } finally {
      setBusy(false);
    }
  };

  const openModal = async (move = null) => {
    setEditMode(!!move);

    if (move) {
      setDestination(move.destination || "");
      setAirline(move.Airline || "");
      setFlightNum(move.FlightNum || "");
      setConfirmation(move.Confirmation || "");
      setPermits(move.Permits || "");
      setDriverId(move.driver_id || "");
      setNotes(move.Notes || "");
      setInventoryId(move.inventory_id || "");
      setRowo(move.rowo || "");
      setChassisVin(move.chassis_vin || "");
      setMoveType(move.move_type || "factory_to_sna");
      setStartDate(move.start_date ? String(move.start_date).slice(0, 10) : "");
      setEndDate(move.end_date ? String(move.end_date).slice(0, 10) : "");
      setDestinationId(move.destination_id || "");
    } else {
      resetForm();
    }

    setSelectedMove(move || {});

    // hydrate for edit preview too
    const invId = toNumId(move?.inventory_id);
    if (invId != null && !(invId in invById)) {
      try {
        const { data } = await api.get(`/all_inventory/${invId}`);
        setInvById((prev) => ({ ...prev, [invId]: data || null }));
      } catch {
        setInvById((prev) => ({ ...prev, [invId]: null }));
      }
    }
  };

  const closeModal = () => {
    setSelectedMove(null);
  };

  const resetForm = () => {
    setDestination("");
    setAirline("");
    setFlightNum("");
    setConfirmation("");
    setPermits("");
    setDriverId("");
    setNotes("");
    setInventoryId("");
    setRowo("");
    setChassisVin("");
    setMoveType("factory_to_sna");
    setStartDate(new Date().toISOString().slice(0, 10));
    setEndDate("");
    setDestinationId("");
  };

  // Search functions
const searchInventory = async () => {
    if (!searchQuery.trim()) return;
    setSearchBusy(true);
    try {
      const term = searchQuery.trim().toLowerCase();
      const { data } = await api.get("all_inventory", { params: { q: searchQuery } });
      const list = Array.isArray(data) ? data : [];

      const filtered = list.filter((r) => {
        const rowo = String(r.rowo || "").toLowerCase();
        const factoryWo = String(r.factory_wo || "").toLowerCase();
        const stockPo = String(r.stock_po || "").toLowerCase();
        const chassisVin = String(r.chassis_vin || "").toLowerCase();
        const bodyVin = String(r.body_vin || "").toLowerCase();
        const prodOrder = String(r.factory_production_so_order || "").toLowerCase();
        return (
          (rowo && rowo.includes(term)) ||
          (factoryWo && factoryWo.includes(term)) ||
          (stockPo && stockPo.includes(term)) ||
          (chassisVin && chassisVin.includes(term)) ||
          (bodyVin && bodyVin.includes(term)) ||
          (prodOrder && prodOrder.includes(term))
        );
      });

      setSearchResults(filtered);
    } catch (e) {
      console.error("Search failed", e);
      setSearchResults([]);
    } finally {
      setSearchBusy(false);
    }
  };

  const selectInventory = async (record) => {
    const id = record?.id ?? "";
    setInventoryId(id);
    setRowo(record?.rowo || "");
    setChassisVin(record?.chassis_vin || "");
    setShowSearchModal(false);

    const invId = toNumId(id);
    if (invId != null) {
      // we already have the record from search results; cache it
      setInvById((prev) => ({ ...prev, [invId]: record || null }));
    }
  };

  const moveCards = useMemo(() => {
    return moves.map((m) => {
      const invId = toNumId(m.inventory_id);
      const invRec = invId != null ? invById[invId] : null;
      return { m, invId, invRec };
    });
  }, [moves, invById]);

  const selectedInvId = toNumId(selectedMove?.inventory_id);
  const selectedInvRec = selectedInvId != null ? invById[selectedInvId] : null;

  return (
    <div className="container">
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
      >
        <h2 style={{ margin: 0, fontWeight: 900 }}>Drive-Away Operations</h2>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: "auto" }}>
          <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} />
          Include completed
        </label>
        <button className="btn-outline" onClick={loadMoves} disabled={busy}>
          Refresh
        </button>
        <button className="btn" onClick={() => openModal()} disabled={busy}>
          Add A New Move
        </button>
      </div>

      {msg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {msg}
        </div>
      ) : null}

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Moves In Progress</div>
        {busy ? <div className="muted">Loading…</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {moveCards.map(({ m, invId, invRec }) => (
            <div key={m.TripId} className="card" style={{ background: m.completed_at ? "rgba(22,163,74,0.04)" : "rgba(255,255,255,0.02)", position: "relative", opacity: m.completed_at ? 0.82 : 1 }}>
              <div
                className="row"
                style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>Move #{m.TripId}</div>
                    <span style={{
                      background: m.move_type === "sna_to_customer" ? "#dcfce7" : "#dbeafe",
                      color:      m.move_type === "sna_to_customer" ? "#166534" : "#1e40af",
                      borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                    }}>
                      {m.move_type === "sna_to_customer" ? "SNA → CUSTOMER" : "FACTORY → SNA"}
                    </span>
                    {openBreakdownByUnit[toNumId(m.inventory_id)] && (
                      <span
                        title={`Case #${openBreakdownByUnit[toNumId(m.inventory_id)].casenumber || "—"} · ${openBreakdownByUnit[toNumId(m.inventory_id)].svcdealer || "no dealer"}`}
                        style={{
                          background: "#fee2e2", color: "#991b1b",
                          borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                        }}
                      >
                        🚨 BROKEN DOWN
                      </span>
                    )}
                    {m.completed_at && (
                      <span
                        title={`Completed ${String(m.completed_at).slice(0, 16).replace('T', ' ')}`}
                        style={{
                          background: "#dcfce7", color: "#166534",
                          borderRadius: 99, padding: "2px 10px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                        }}
                      >
                        ✓ COMPLETED {String(m.completed_at).slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Inventory Id: {m.inventory_id || "(none)"} • Location: {m.destination || "(none)"} • Driver:{" "}
                    {drvById[m.driver_id]?.FullName || "(none)"}
                  </div>
                  {(m.start_date || m.end_date) && (() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const sd = m.start_date ? String(m.start_date).slice(0, 10) : null;
                    const isFuture = sd && sd > today;
                    const isToday  = sd && sd === today;
                    const color = isFuture ? "#2563eb" : isToday ? "#16a34a" : "#6b7280";
                    return (
                      <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color }}>
                        {isFuture && "📅 Scheduled for "}
                        {isToday  && "🚛 Scheduled today — "}
                        {!isFuture && !isToday && sd && "Started "}
                        {sd}
                        {m.end_date && ` → ${String(m.end_date).slice(0, 10)}`}
                      </div>
                    );
                  })()}
                </div>

                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button className="btn-outline" onClick={() => openModal(m)} disabled={busy}
                    title={m.completed_at ? "Edit destination / notes (completed moves stay in history)" : "Edit"}>
                    Edit
                  </button>
                  <button className="btn-outline" onClick={() => deleteMove(m.TripId)} disabled={busy}>
                    Delete
                  </button>
                  {!m.completed_at && (
                    openBreakdownByUnit[toNumId(m.inventory_id)] ? (
                      <button
                        className="btn"
                        style={{ background: "#059669", color: "#fff" }}
                        onClick={() => setResolveBreakdown(openBreakdownByUnit[toNumId(m.inventory_id)])}
                        disabled={busy}
                        title="Mark unit as repaired and ready for pickup from repair"
                      >
                        Mark Repaired
                      </button>
                    ) : (
                      <button
                        className="btn-outline"
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                        onClick={() => setBreakdownMove(m)}
                        disabled={busy}
                        title="Report a breakdown for this unit"
                      >
                        🚨 Breakdown
                      </button>
                    )
                  )}
                  {!m.completed_at && (
                    <button className="btn" onClick={() => completeMove(m.TripId, m.move_type)} disabled={busy || openBreakdownByUnit[toNumId(m.inventory_id)]}>
                      {m.move_type === "sna_to_customer" ? "Mark Delivered" : "Mark Arrived at SNA"}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                {/* ✅ render full inventory record when available */}
                <CardComponent record={invRec || (invId != null ? { id: invId } : {})} table="all_inventory" />
                {invId != null && invRec === null ? (
                  <div className="muted" style={{ marginTop: 8 }}>
                    Couldn’t load inventory record #{invId}.
                  </div>
                ) : null}
              </div>

              {/* Hover Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,0.5)",
                  display: "none",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  flexDirection: "column",
                }}
                className="overlay"
                onMouseEnter={(e) => e.currentTarget.style.display = "flex"}
                onMouseLeave={(e) => e.currentTarget.style.display = "none"}
              >
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 13)} disabled={busy}>
                  Broke Down
                </button>
                <button className="btn" onClick={() => endMove(m.TripId, m.inventory_id, 3)} disabled={busy}>
                  Complete Move
                </button>
              </div>
            </div>
          ))}

          {!busy && moves.length === 0 ? <div className="muted">No moves found.</div> : null}
        </div>
      </div>

      {selectedMove && (
        <Modal onClose={closeModal}>
          <h2>{editMode ? "Edit Move" : "Create Move"}</h2>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <select
              className="input"
              value={moveType}
              onChange={(e) => setMoveType(e.target.value)}
              title="Determines what happens on Complete Move"
            >
              <option value="factory_to_sna">Factory → SNA (pickup + transport in)</option>
              <option value="sna_to_customer">SNA → Customer (final delivery, requires PDI complete)</option>
            </select>

            <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
              <label className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {moveType === "sna_to_customer" ? "Scheduled Departure from SNA" : "Scheduled Pickup Date"}
              </label>
              <input
                className="input" type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div style={{ display: "grid", gap: 4, minWidth: 200 }}>
              <label className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {moveType === "sna_to_customer" ? "Expected Delivery to Customer" : "Expected Arrival at SNA"} <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
              </label>
              <input
                className="input" type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center", flex: "1 1 260px", minWidth: 220 }}>
              <select
                className="input"
                value={destinationId}
                onChange={(e) => {
                  const v = e.target.value;
                  setDestinationId(v);
                  // Populate the free-text field with the destination's name
                  // so legacy views still show something sensible. Server also
                  // overrides from destination_id on save.
                  if (v) {
                    const picked = destinations.find(d => String(d.id) === String(v));
                    if (picked) setDestination(picked.name);
                  }
                }}
                style={{ flex: 1 }}
                title="Pick from destinations (admin → Destinations)"
              >
                <option value="">— Destination (pick from list) —</option>
                {destinations.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.city ? ` · ${d.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="input"
              placeholder={destinationId ? "Override name (optional)" : "Or type a destination"}
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
            <input className="input" placeholder="Airline" value={airline} onChange={(e) => setAirline(e.target.value)} />
            <input className="input" placeholder="Flight Num" value={flightNum} onChange={(e) => setFlightNum(e.target.value)} />
            <input className="input" placeholder="Confirmation" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
            <input className="input" placeholder="Permits" value={permits} onChange={(e) => setPermits(e.target.value)} />

            <select className="input" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Select Driver</option>
              {(() => {
                // Sort: drivers whose home_terminal matches a word in the
                // destination float to the top. This is soft — user can still
                // pick anyone.
                const dest = String(destination || "").toLowerCase();
                const match = (d) => {
                  const t = String(d.home_terminal || "").toLowerCase();
                  return !!t && dest && dest.includes(t);
                };
                const sorted = [...drivers].sort((a, b) => {
                  const am = match(a), bm = match(b);
                  if (am !== bm) return am ? -1 : 1;
                  return String(a.FullName || "").localeCompare(String(b.FullName || ""));
                });
                return sorted.map((drv) => {
                  const terminal = drv.home_terminal ? ` · ${drv.home_terminal}` : "";
                  const star = match(drv) ? " ★" : "";
                  return (
                    <option key={drv.DriverId} value={drv.DriverId}>
                      {(drv.FullName || `Driver #${drv.DriverId}`) + terminal + star}
                    </option>
                  );
                });
              })()}
            </select>

            <input className="input" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="row" style={{ gap: 10, alignItems: "center" }}>
              <input className="input" placeholder="Inventory ID" value={inventoryId} readOnly />
              <button className="btn-outline" onClick={() => setShowSearchModal(true)} disabled={busy}>
                Search Inventory
              </button>
            </div>

            <input className="input" placeholder="RO WO" value={rowo} onChange={(e) => setRowo(e.target.value)} />
            <input className="input" placeholder="Chassis VIN" value={chassisVin} onChange={(e) => setChassisVin(e.target.value)} />

            <button className="btn" onClick={createOrUpdateMove} disabled={busy}>
              {editMode ? "Update" : "Create"}
            </button>
          </div>

          {editMode ? (
            <div style={{ marginTop: 20 }}>
              <CardComponent
                record={selectedInvRec || (selectedInvId != null ? { id: selectedInvId } : {})}
                table="all_inventory"
              />
              {selectedInvId != null && selectedInvRec === null ? (
                <div className="muted" style={{ marginTop: 8 }}>
                  Couldn’t load inventory record #{selectedInvId}.
                </div>
              ) : null}
            </div>
          ) : null}

          <button className="btn-outline" onClick={closeModal} disabled={busy}>
            Close
          </button>
        </Modal>
      )}

      {breakdownMove && (
        <ReportBreakdownModal
          move={breakdownMove}
          invRec={invById[toNumId(breakdownMove.inventory_id)] || null}
          onClose={() => setBreakdownMove(null)}
          onSaved={async () => {
            setBreakdownMove(null);
            setMsg("Breakdown reported.");
            await Promise.all([loadMoves(), loadOpenBreakdowns()]);
          }}
        />
      )}

      {resolveBreakdown && (
        <ResolveBreakdownModal
          breakdown={resolveBreakdown}
          onClose={() => setResolveBreakdown(null)}
          onSaved={async () => {
            setResolveBreakdown(null);
            setMsg("Breakdown resolved — unit marked ready for pickup from repair.");
            await loadOpenBreakdowns();
          }}
        />
      )}

      {showSearchModal && (
        <Modal onClose={() => setShowSearchModal(false)}>
          <h2>Search Inventory</h2>

          <div className="row" style={{ gap: 10 }}>
            <input
              className="input"
              placeholder="Search by RO/WO, Factory WO, Stock PO"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="btn" onClick={searchInventory} disabled={searchBusy}>
              Search
            </button>
          </div>

          {searchBusy ? <div className="muted">Searching...</div> : null}

          <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
            {searchResults.map((result) => (
              <div
                key={result.id}
                className="card"
                style={{ cursor: "pointer", background: "rgba(255,255,255,0.02)" }}
                onClick={() => selectInventory(result)}
              >
                <div>ID: {result.id}</div>
                <div>RO/WO: {result.rowo || "(none)"}</div>
                <div>Factory WO: {result.factory_wo || "(none)"}</div>
                <div>Stock PO: {result.stock_po || "(none)"}</div>
              </div>
            ))}
            {searchResults.length === 0 && !searchBusy ? <div className="muted">No results found.</div> : null}
          </div>

          <button className="btn-outline" onClick={() => setShowSearchModal(false)}>
            Close
          </button>
        </Modal>
      )}
    </div>
  );
}

// ── Report Breakdown modal ──────────────────────────────────────────────────
function ReportBreakdownModal({ move, invRec, onClose, onSaved }) {
  const [location, setLocation]     = useState("");
  const [svcdealer, setSvcdealer]   = useState("");
  const [caseVendor, setCaseVendor] = useState("MackOne");
  const [casenumber, setCasenumber] = useState("");
  const [reason, setReason]         = useState("");
  const [adHocInput, setAdHocInput] = useState("");
  const [adHocList, setAdHocList]   = useState([]);
  const [releaseDriver, setReleaseDriver] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState("");

  const addEmail = () => {
    const v = adHocInput.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErr("Invalid email address"); return; }
    if (adHocList.includes(v)) { setAdHocInput(""); return; }
    setAdHocList([...adHocList, v]); setAdHocInput(""); setErr("");
  };

  const removeEmail = (e) => setAdHocList(adHocList.filter(x => x !== e));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/units/${move.inventory_id}/breakdown`, {
        move_id:            move.TripId,
        driver_id:          move.driver_id || null,
        location:           location.trim(),
        svcdealer:          svcdealer.trim(),
        case_vendor:        caseVendor,
        casenumber:         casenumber.trim(),
        reason:             reason.trim(),
        ad_hoc_recipients:  adHocList,
        release_driver:     releaseDriver,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Report failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0, color: "#dc2626" }}>🚨 Report Breakdown</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Move #{move.TripId}{invRec?.rowo ? ` · Unit ${invRec.rowo}` : ""}{invRec?.stock_po ? ` · Stock/PO ${invRec.stock_po}` : ""}
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Location (where the truck is)</label>
          <input className="input" style={{ width: "100%" }} value={location} onChange={e => setLocation(e.target.value)}
            placeholder="e.g. I-40 MM 284, Amarillo TX" />
        </div>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Servicing Dealer</label>
          <input className="input" style={{ width: "100%" }} value={svcdealer} onChange={e => setSvcdealer(e.target.value)}
            placeholder="e.g. Kenworth of Dallas" />
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div style={{ flex: "0 0 160px" }}>
            <label className="muted" style={{ fontSize: 12 }}>Case Vendor</label>
            <select className="input" style={{ width: "100%" }} value={caseVendor} onChange={e => setCaseVendor(e.target.value)}>
              <option>MackOne</option>
              <option>Volvo Action Service</option>
              <option>Peterbilt PACCAR Solutions</option>
              <option>Kenworth TruckTech+</option>
              <option>Other</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="muted" style={{ fontSize: 12 }}>Case #</label>
            <input className="input" style={{ width: "100%" }} value={casenumber} onChange={e => setCasenumber(e.target.value)}
              placeholder="Roadside case number" />
          </div>
        </div>
        <div>
          <label className="muted" style={{ fontSize: 12 }}>Complaint / Reason</label>
          <textarea className="input" style={{ width: "100%", minHeight: 80 }} value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Check engine light + loss of power, towed to dealer" />
        </div>
        {move.driver_id && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 10, padding: 12,
            border: `1px solid ${releaseDriver ? "#dc2626" : "var(--card-border)"}`,
            borderRadius: 8,
            background: releaseDriver ? "rgba(220,38,38,0.08)" : "transparent",
          }}>
            <input
              type="checkbox"
              id="release_driver_cb"
              checked={releaseDriver}
              onChange={e => setReleaseDriver(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <label htmlFor="release_driver_cb" style={{ cursor: "pointer", flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Release driver from this move</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.4 }}>
                Leave <strong>unchecked</strong> when the driver will wait (hotel) while the unit is repaired —
                they stay assigned and the move stays open. Check only when the repair will exceed tolerable wait time
                and the driver should go home. A new driver (or the same one) can be assigned later to pick up from repair.
              </div>
            </label>
          </div>
        )}

        <div>
          <label className="muted" style={{ fontSize: 12 }}>
            Ad-hoc notification emails <span style={{ fontWeight: 400 }}>(in addition to the default recipients for this event)</span>
          </label>
          <div className="row" style={{ gap: 6 }}>
            <input className="input" style={{ flex: 1 }} value={adHocInput}
              onChange={e => setAdHocInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
              placeholder="someone@example.com and press Add" />
            <button className="btn-outline" onClick={addEmail}>Add</button>
          </div>
          {adHocList.length > 0 && (
            <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {adHocList.map(e => (
                <span key={e} style={{ background: "#fef2f2", color: "#991b1b", borderRadius: 99, padding: "4px 10px", fontSize: 12, fontWeight: 600 }}>
                  {e}
                  <button onClick={() => removeEmail(e)} style={{ marginLeft: 6, background: "none", border: "none", color: "#991b1b", cursor: "pointer", fontWeight: 700 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn" style={{ background: "#dc2626" }} onClick={submit} disabled={busy}>
          {busy ? "Reporting…" : "Report Breakdown"}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Reporting stamps the unit as broken down and sends the breakdown notification to the default recipients
        plus any ad-hoc emails above. The driver stays assigned by default — check <strong>Release driver</strong> above
        only when the driver is going home and not waiting for the repair.
      </div>
    </Modal>
  );
}

// ── Resolve Breakdown modal (mark repaired) ────────────────────────────────
function ResolveBreakdownModal({ breakdown, onClose, onSaved }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post(`/breakdowns/${breakdown.id}/resolve`, { resolution_notes: notes.trim() || null });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Resolve failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 style={{ marginTop: 0, color: "#059669" }}>✅ Mark Repaired</h2>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Unit {breakdown.rowo || `#${breakdown.inventory_id}`} · Case {breakdown.casenumber || "—"} · {breakdown.svcdealer || "no dealer"}
      </div>
      {breakdown.reason && (
        <div className="card" style={{ background: "#fef2f2", marginBottom: 12, fontSize: 13 }}>
          <strong>Original complaint:</strong> {breakdown.reason}
        </div>
      )}

      <label className="muted" style={{ fontSize: 12 }}>Repair notes (optional)</label>
      <textarea className="input" style={{ width: "100%", minHeight: 100 }} value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="e.g. Replaced turbo, road-tested, cleared codes" />

      {err && <div className="card" style={{ marginTop: 10, color: "#dc2626" }}>{err}</div>}

      <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button className="btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn" style={{ background: "#059669" }} onClick={submit} disabled={busy}>
          {busy ? "Saving…" : "Mark Repaired"}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Marks the unit ready for pickup from repair and sends the <code>unit_repaired</code> notification
        to the default recipients plus anyone who was ad-hoc notified when the breakdown was first reported.
        Assign a driver to the move when you're ready to pick up from the dealer.
      </div>
    </Modal>
  );
}
